<template>
  <div>
    <!-- Tela normal: filtros + Executar -->
    <div v-if="!props.printMode" class="mb-3">
      <h6>Relação de cavalos em aulas</h6>
      <div class="row g-3 align-items-end">
        <div class="col-auto">
          <label>Data inicial</label>
          <input type="date" v-model="dataInicio" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data final</label>
          <input type="date" v-model="dataFim" class="form-control" />
        </div>
        <div class="col-auto">
          <button class="btn btn-primary" :disabled="!dataInicio || !dataFim" @click="abrirPrint">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- Modo impressão -->
    <div v-else>
        <!-- Subtítulo via query (o Wrapper já sabe exibir subtítulo em outros relatórios,
            mas mantemos aqui também para garantir visual) -->
        <p v-if="route.query?.subtitle" class="text-center" style="font-size:14px;">
            {{ route.query.subtitle }}
        </p>

        <div v-if="loading" class="text-center">Carregando…</div>
        <div v-else>
        <div v-if="Object.keys(dados).length === 0" class="text-center">Sem resultados.</div>
        
        <!-- RESUMO POR CAVALO (primeira tabela => 1ª aba no XLSX) -->
        <div v-else class="mb-3">
            <h6 class="mb-2">Resumo por Cavalo</h6>
            <table class="table table-striped">
            <thead>
                <tr>
                <th>Cavalo</th>
                <th style="width:180px;">Quantidade de Aulas</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(r, i) in resumoCavalos.linhas" :key="'resumo-' + i">
                <td>{{ r.cavalo }}</td>
                <td>{{ r.quantidade }}</td>
                </tr>
            </tbody>
            <tfoot>
                <tr>
                <td><strong>Total ({{ resumoCavalos.cavalos }} cavalos)</strong></td>
                <td><strong>{{ resumoCavalos.total }}</strong></td>
                </tr>
            </tfoot>
            </table>
        </div>

        <div v-for="(linhas, cavalo) in dados" :key="cavalo" class="mb-4">
          <h6 class="mb-2">Cavalo: {{ cavalo }}</h6>
          <table class="table table-striped">
            <thead>
              <tr>
                <th style="width:130px;">Data</th>
                <th style="width:100px;">Turma</th>
                <th style="width:160px;">Nível</th>
                <th style="width:110px;">Matrícula</th>
                <th>Instrutor</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in linhas" :key="cavalo + '-' + i">
                <td>{{ formatarData(row.data_aula) }}</td>
                <td>{{ row.turma_id }}</td>
                <td>{{ row.nivel }}</td>
                <td>{{ row.matricula_id ?? '—' }}</td>
                <td>{{ row.instrutor }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '@/services/axios'

const props = defineProps({
  printMode:  { type: Boolean, default: false },
  printTitle: { type: String,  default: '' },
  subtitle:   { type: String,  default: '' }
})

const router = useRouter()
const route  = useRoute()

// Tela normal
const dataInicio = ref('')
const dataFim    = ref('')

// Print
const loading = ref(false)
const dados   = ref({})

// Resumo por Cavalo + totais
const resumoCavalos = computed(() => {
  const linhas = []
  let total = 0
  for (const [cavalo, arr] of Object.entries(dados.value || {})) {
    const qtd = Array.isArray(arr) ? arr.length : 0
    linhas.push({ cavalo, quantidade: qtd })
    total += qtd
  }
  // ordena por quantidade (desc) e depois por nome
  linhas.sort((a, b) => (b.quantidade - a.quantidade) || a.cavalo.localeCompare(b.cavalo))
  return { linhas, total, cavalos: linhas.length }
})

function formatarData(d) {
  // espera 'YYYY-MM-DD' e converte para 'DD/MM/YYYY' sem usar Date()
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-')
    return `${day}/${m}/${y}`
  }
  // fallback leve (caso venha outro formato)
  const dt = new Date(d)
  if (!Number.isNaN(dt.getTime())) {
    const dd = String(dt.getDate()).padStart(2,'0')
    const mm = String(dt.getMonth()+1).padStart(2,'0')
    const yy = dt.getFullYear()
    return `${dd}/${mm}/${yy}`
  }
  return String(d ?? '')
}

function abrirPrint() {
  if (!dataInicio.value || !dataFim.value) return
  const sub = `( ${formatarData(dataInicio.value)} a ${formatarData(dataFim.value)} )`
  router.push({
    path: '/gestao/relatorios/print/cavalos-aulas',
    query: {
      title:    'Relação de cavalos em aulas',
      subtitle: sub,                   // seu Wrapper já exibe subtítulo em outros relatórios
      dataInicio: dataInicio.value,
      dataFim:    dataFim.value
    }
  })
}

onMounted(async () => {
  if (!props.printMode) return
  const di = route.query?.dataInicio
  const df = route.query?.dataFim
  if (!di || !df) return

  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/operacionais/cavalos-aulas', {
      params: { dataInicio: di, dataFim: df }
    })
    dados.value = res.data.data || {}
  } catch (e) {
    console.error('Erro ao carregar Cavalos x Aulas:', e)
    dados.value = {}
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
table { table-layout: fixed; width: 100%; font-size: .75rem; }
th, td { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
